import { FORMAT_RULES, FORMATS, type Format, type QuestionType } from '@quizee/core';

/** Turn wire values into the words an editor reads on screen. */

const RULES: Record<string, { label: string } | undefined> = FORMAT_RULES;

export function formatLabel(format: string): string {
  return RULES[format]?.label ?? format;
}

export function isFormat(value: string): value is Format {
  return (FORMATS as readonly string[]).includes(value);
}

/**
 * What each format is, in an editor's words rather than the schema's.
 *
 * The shapes and the results columns come from the format table in docs/SPEC.md and from
 * FORMAT_RULES; the second line is the newsroom reason to reach for it, which is the part a
 * constraints table cannot tell anybody.
 */
const FORMAT_COPY: Readonly<Record<Format, { what: string; when: string; example: string }>> = {
  poll: {
    what: 'One question with a few answers. Readers see the percentages as they vote.',
    when: 'Use it for a quick reaction to a story.',
    example:
      'A poll on our story about the Hull bus station rebuild, asking readers whether 12 million pounds is money well spent.',
  },
  trivia_quiz: {
    what: 'Questions with right and wrong answers. Readers get a score out of the total.',
    when: 'Use it to test what readers know about something you have covered.',
    example:
      'Eight questions on our Sheffield tram extension story: when it opens, which stops it adds and what the fares will be.',
  },
  personality_quiz: {
    what: 'Questions whose answers point at outcomes. Each reader lands on one of them.',
    when: 'Use it for a shareable "which one are you" piece.',
    example:
      'Which Leeds neighbourhood should you live in? Six questions on budget, the commute and how much noise you can put up with.',
  },
  survey: {
    what: 'Any mix of questions, nothing scored. Readers get a thank you, not results.',
    when: 'Use it to ask your audience something and read the answers yourself.',
    example:
      'Ask readers how they get into the city centre, how long it takes them, and what would make them use the bus instead.',
  },
  form: {
    what: 'Boxes readers type into, with a receipt when they send it.',
    when: 'Use it for tip-offs, competition entries and letters to the editor.',
    example:
      'A form for readers to send us their flooding photographs, with their name, where the picture was taken and how we can reach them.',
  },
};

export interface FormatChoice {
  readonly format: Format;
  readonly label: string;
  readonly what: string;
  readonly when: string;
  readonly example: string;
}

/** Every format a publisher can start, in the order the picker offers them. */
export const FORMAT_CHOICES: readonly FormatChoice[] = FORMATS.map((format) => ({
  format,
  label: FORMAT_RULES[format].label,
  ...FORMAT_COPY[format],
}));

export function formatWhat(format: Format): string {
  return FORMAT_COPY[format].what;
}

/**
 * What a description of this format might look like, shown as the placeholder on the drafting
 * screen. A real sentence about a real story teaches the tool in a way that "describe your quiz"
 * cannot, and it changes with the format because the useful example does.
 */
export function promptExample(format: Format): string {
  return FORMAT_COPY[format].example;
}

const QUESTION_TYPE_LABELS: Readonly<Record<QuestionType, string>> = {
  single_choice: 'Readers pick one answer',
  multiple_choice: 'Readers pick any number of answers',
  free_text: 'Readers type an answer',
  scale: 'Readers pick a number on a scale',
};

export function questionTypeLabel(type: QuestionType): string {
  return QUESTION_TYPE_LABELS[type];
}

const STATUS_LABELS: Record<string, string | undefined> = {
  draft: 'Draft',
  published: 'Live',
  archived: 'Archived',
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/**
 * Where a newsletter signup has got to, in a publisher's words.
 *
 * Pending is written out as what it actually is. "Pending" on its own reads like a fault, and this
 * one is a reader who has not opened an email yet, which is normal and mostly resolves itself.
 */
const SUBSCRIPTION_STATUS_LABELS: Record<string, string | undefined> = {
  subscribed: 'Subscribed',
  pending: 'Waiting to confirm',
  unsubscribed: 'Unsubscribed',
};

export function subscriptionStatusLabel(status: string): string {
  return SUBSCRIPTION_STATUS_LABELS[status] ?? status;
}

const ROLE_LABELS: Record<string, string | undefined> = {
  owner: 'Owner',
  editor: 'Editor',
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

const WHEN = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function whenLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'Unknown';
  return WHEN.format(at);
}

const DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * A date with no time on it: "6 October 2026".
 *
 * For the things that happen on a day rather than at a moment, such as when a subscription
 * renews. A minute past midnight printed beside a renewal invites somebody to wonder what happens
 * at 00:01, and the answer is nothing they need to know.
 */
export function dayLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'Unknown';
  return DAY.format(at);
}

/** Whole number percentages that never divide by zero. */
export function percentage(count: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((count / total) * 100);
}
